// Location Overview — the summary cards + online provider breakdown
// previously shown on the /reports Overview tab. Filed under Accounting
// so nothing is lost when the directory replaced the old tab UI.
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ReportShell from "./ReportShell";

const ONLINE_PROVIDER_LABEL = { stripe: "Stripe", paypal: "PayPal" };

export default function OverviewReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    Promise.all([
      staffApi.get("/branches"),
      staffApi.get(`/reports/branch/${bid}/summary`),
    ]).then(([b, s]) => {
      setBranch(b.data.find((x) => x.id === bid) || null);
      setSummary(s.data);
    }).catch((err) => toast.error(formatApiError(err)));
  }, [bid]);

  const cards = summary ? [
    { label: "Revenue (recorded)", value: formatBranchMoney(branch, summary.revenue_total) },
    { label: "Orders", value: summary.orders_count },
    { label: "Appointments", value: summary.appointments_count },
    { label: "Customers (brand-wide)", value: summary.customers_count },
  ] : [];

  // Export flattens the summary tiles + per-provider rows into a single
  // table so accountants can paste headline numbers straight into email.
  // Iter-318 · Every metric — money or count — flows as a raw number so
  // `=SUM()` works on the whole column.
  const exportSpec = useMemo(() => {
    if (!summary) return null;
    const money = (v) => formatBranchMoney(branch, v || 0);
    // Value column carries both money and count metrics — use a formatter
    // that formats the money-flagged rows with a currency label and
    // renders count rows as plain integers.
    const formatValue = (v) => (typeof v === "number" ? money(v) : String(v));
    const rows = [
      { metric: "Revenue (recorded, net)", value: Number(summary.revenue_total) || 0 },
      { metric: "Revenue gross", value: Number(summary.revenue_gross) || 0 },
      { metric: "Refunds total", value: Number(summary.refunds_total) || 0 },
      { metric: "Orders (settled)", value: Number(summary.orders_count) || 0 },
      { metric: "Appointments", value: Number(summary.appointments_count) || 0 },
      { metric: "Customers (brand-wide)", value: Number(summary.customers_count) || 0 },
    ];
    (summary.online_by_provider || []).forEach((p) => {
      rows.push({
        metric: `Online · ${ONLINE_PROVIDER_LABEL[p.provider] || p.provider} (${p.count} payment${p.count === 1 ? "" : "s"})`,
        value: Number(p.amount) || 0,
      });
    });
    return {
      filename: "location-overview",
      range: null,
      columns: [
        { header: "Metric", key: "metric" },
        { header: "Value",  key: "value", numeric: true, format: formatValue, align: "right" },
      ],
      rows,
    };
  }, [summary, branch]);

  return (
    <ReportShell title="Location Overview" subtitle="Lifetime headline numbers for this branch plus a Stripe / PayPal reconciliation panel." branchName={branch?.name} tab="accounting" exportSpec={exportSpec}>
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c) => (
            <Card key={c.label} className="rounded-lg" data-testid={`overview-card-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <CardContent className="p-5">
                <p className="tracking-overline text-[10px] text-muted-foreground">{c.label}</p>
                <p className="text-2xl font-semibold mt-2">{c.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {summary && (
          <Card className="rounded-lg" data-testid="online-provider-breakdown-card">
            <CardContent className="p-5">
              <div className="mb-3">
                <p className="tracking-overline text-[10px] text-muted-foreground">Online Payments · by Provider</p>
                <p className="text-sm text-muted-foreground mt-1">Every payment recorded via the Checkout <span className="font-medium">Online</span> tab is tagged with its processor for reconciliation.</p>
              </div>
              {(summary.online_by_provider || []).length === 0 ? (
                <p className="text-sm text-muted-foreground py-4" data-testid="online-provider-empty">No online payments recorded yet at this branch.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {summary.online_by_provider.map((row) => (
                    <div key={row.provider} className="flex items-center justify-between rounded-md border border-border p-3" data-testid={`online-provider-row-${row.provider}`}>
                      <div className="flex items-center gap-2">
                        <Badge className="rounded-full bg-indigo-100 text-indigo-800 border-indigo-200">{ONLINE_PROVIDER_LABEL[row.provider] || row.provider}</Badge>
                        <span className="text-xs text-muted-foreground">{row.count} payment{row.count === 1 ? "" : "s"}</span>
                      </div>
                      <span className="font-semibold text-sm">{formatBranchMoney(branch, row.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </ReportShell>
  );
}
